import { useMemo, useState } from 'react'
import { MessageSquare } from 'lucide-react'
import type { ChatOverview } from '@/types/teacher-chat'
import { chatMatchesListFilter } from '@/utils/chatHelpers'
import { ChatSearchInput } from './ChatSearchInput'
import { ChatFilterPills, type ChatFilter } from './ChatFilterPills'
import { ChatGroup } from './ChatGroup'

interface ChatListPanelProps {
  chats: ChatOverview[]
  selectedRequestId: string | null
  onSelectChat: (requestId: string) => void
  isLoading: boolean
}

export function ChatListPanel({ chats, selectedRequestId, onSelectChat, isLoading }: ChatListPanelProps) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ChatFilter>('not_cancelled')

  const filteredChats = useMemo(() => {
    let result = chats

    result = result.filter(c => chatMatchesListFilter(c, filter))

    if (search.trim()) {
      const q = search.toLowerCase().trim()
      result = result.filter(c =>
        (c.subject_name || '').toLowerCase().includes(q) ||
        (c.klass_name || '').toLowerCase().includes(q) ||
        c.participants.some(p => p.teacher_name.toLowerCase().includes(q))
      )
    }

    return result
  }, [chats, filter, search])

  return (
    <div className="w-80 max-w-full bg-white border-r border-gray-200 flex flex-col shrink-0 min-h-0">
      <ChatSearchInput value={search} onChange={setSearch} />
      <ChatFilterPills filter={filter} onChange={setFilter} />

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-4 space-y-3 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg" />
            ))}
          </div>
        )}

        {!isLoading && filteredChats.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Keine Chats gefunden</p>
          </div>
        )}

        {!isLoading && filteredChats.length > 0 && (
          filteredChats.map(chat => (
            <ChatGroup
              key={chat.request_id}
              chat={chat}
              isSelected={chat.request_id === selectedRequestId}
              defaultExpanded={!chat.is_archived}
              onSelect={() => onSelectChat(chat.request_id)}
            />
          ))
        )}
      </div>
    </div>
  )
}
